import React, { useState } from "react";
import { Minus, Plus } from "lucide-react";

const SIZES = {
  default: { btn: 34, input: 56, font: 14 },
  sm: { btn: 30, input: 48, font: 13 },
};

// Controlled, digit-only stepper. Deliberately mirrors the value contract
// already used by PQCMemberPage's inline stepper (line.quantity, never
// line.quantity || "") so a cleared/zeroed field renders "0" instead of
// blanking — see the falsy-zero input bug this replaces.
export default function QuantityStepper({
  value,
  onChange,
  min = 0,
  max,
  disabled = false,
  error = false,
  size = "default",
}) {
  const [hovered, setHovered] = useState(null); // "dec" | "inc" | null
  const dims = SIZES[size] || SIZES.default;
  const numericValue = Number(value) || 0;
  const atMin = numericValue <= min;
  const atMax = max != null && numericValue >= max;

  const commit = (next) => {
    const clamped = Math.max(min, max != null ? Math.min(max, next) : next);
    onChange(clamped);
  };

  const handleInput = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    commit(digits === "" ? 0 : parseInt(digits, 10));
  };

  const stepBtnStyle = (key, isDisabled) => ({
    width: dims.btn,
    height: dims.btn,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: isDisabled ? "#1a1a1c" : hovered === key ? "#26262a" : "#1e1e1e",
    border: "none",
    color: isDisabled ? "#4a4a4e" : "#c3c8cc",
    cursor: isDisabled ? "not-allowed" : "pointer",
    flexShrink: 0,
    outline: "none",
    transition: "background 0.12s",
  });

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        height: dims.btn,
        borderRadius: 8,
        border: `1px solid ${error ? "#f27059" : "rgba(255,255,255,0.14)"}`,
        overflow: "hidden",
        opacity: disabled ? 0.5 : 1,
        flexShrink: 0,
      }}
    >
      <button
        type="button"
        aria-label="decrease quantity"
        disabled={disabled || atMin}
        onClick={() => commit(numericValue - 1)}
        onMouseEnter={() => setHovered("dec")}
        onMouseLeave={() => setHovered(null)}
        style={stepBtnStyle("dec", disabled || atMin)}
      >
        <Minus size={13} />
      </button>
      <input
        type="text"
        inputMode="numeric"
        aria-label="quantity"
        value={numericValue}
        disabled={disabled}
        onChange={handleInput}
        style={{
          width: dims.input,
          height: dims.btn,
          textAlign: "center",
          background: "#000",
          border: "none",
          borderLeft: "1px solid rgba(255,255,255,0.14)",
          borderRight: "1px solid rgba(255,255,255,0.14)",
          color: "#fff",
          fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
          fontSize: dims.font,
          fontWeight: 700,
          outline: "none",
        }}
      />
      <button
        type="button"
        aria-label="increase quantity"
        disabled={disabled || atMax}
        onClick={() => commit(numericValue + 1)}
        onMouseEnter={() => setHovered("inc")}
        onMouseLeave={() => setHovered(null)}
        style={stepBtnStyle("inc", disabled || atMax)}
      >
        <Plus size={13} />
      </button>
    </div>
  );
}
